import { observer } from 'mobx-react';
import { useRef, useCallback, useState, useContext } from 'react';
import { T, TranslationsContext, translation } from '../../../../../../Translations';
import { ValidationRules } from '../utils/ValidationRules';

interface Props {
  name: string;
  handleLearningCollectionNameChange: (event: string) => void;
  compact?: boolean;
}

const MAX_LENGTH = 255;

/**
 * Inline editable title field matching Rvstyleguide pattern
 *
 * Features:
 * - Click-to-edit with auto-focus
 * - Save/Cancel buttons in floating action panel
 * - 28px font size (16px in compact), semibold weight, 1.3 line height
 * - Validation for required and max length
 */
function Title({ name, handleLearningCollectionNameChange, compact = false }: Props) {
  const ts = useContext(TranslationsContext);
  const [isEditing, setIsEditing] = useState(false);
  const [draftValue, setDraftValue] = useState(name);
  const inputRef = useRef<HTMLInputElement>(null);

  // Reset draft when prop changes (adjust during render)
  const [prevName, setPrevName] = useState(name);
  if (name !== prevName) {
    setPrevName(name);
    setDraftValue(name);
  }

  const handleSave = useCallback(() => {
    const trimmedValue = draftValue.trim();
    if (trimmedValue && trimmedValue !== name) {
      handleLearningCollectionNameChange(trimmedValue);
    }
    setIsEditing(false);
  }, [draftValue, name, handleLearningCollectionNameChange]);

  const handleCancel = useCallback(() => {
    setDraftValue(name);
    setIsEditing(false);
  }, [name]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleCancel();
      } else if (e.key === 'Enter') {
        e.preventDefault();
        handleSave();
      }
    },
    [handleSave, handleCancel],
  );

  const handleBlur = () => {
    // Delay to allow button clicks to register
    setTimeout(() => {
      if (isEditing) {
        handleSave();
      }
    }, 150);
  };

  // Validation
  const isEmpty = ValidationRules.isEmpty(draftValue);
  const exceedsMaxLength = ValidationRules.exceedsMaxLength(draftValue, MAX_LENGTH);
  const showRequiredError = isEmpty && !isEditing;
  const showMaxLengthError = exceedsMaxLength;

  const fontSize = compact ? 'var(--text-base)' : '28px';
  const intercomTarget = compact
    ? 'builder-v2-collection-title-field-compact'
    : 'builder-v2-collection-title-field';

  if (!isEditing) {
    return (
      <div className={compact ? 'min-w-0' : undefined}>
        <div
          onClick={() => {
            setIsEditing(true);
            setDraftValue(name);
            requestAnimationFrame(() => {
              inputRef.current?.focus();
              inputRef.current?.select();
            });
          }}
          className="cursor-pointer rounded px-2 py-1.5 -mx-2 -my-1.5 transition-colors"
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = 'rgba(234, 234, 235, 0.5)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
          }}
          data-intercom-target={intercomTarget}
        >
          <span
            className={compact ? 'block truncate' : undefined}
            style={{
              fontSize,
              fontWeight: 'var(--font-weight-semibold)',
              lineHeight: '1.3',
            }}
          >
            {name || (
              <span className="text-muted-foreground">
                <T kind="New Learning Collection" />
              </span>
            )}
          </span>
        </div>
        {showRequiredError && (
          <div className="mt-1 text-sm text-destructive">
            <T kind="Name is required" />
          </div>
        )}
        {showMaxLengthError && (
          <div className="mt-1 text-sm text-destructive">
            <T kind="May not exceed {{maxLength}} characters" maxLength={MAX_LENGTH} />
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={`relative ${compact ? 'min-w-0' : ''}`}>
      <input
        ref={inputRef}
        type="text"
        value={draftValue}
        onChange={(e) => setDraftValue(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        maxLength={MAX_LENGTH}
        className="w-full px-2 py-1.5 rounded focus:outline-none bg-card border-2 border-primary"
        style={{
          fontSize,
          fontWeight: 'var(--font-weight-semibold)',
          lineHeight: '1.3',
        }}
        placeholder={translation('New Learning Collection')(ts)}
        aria-label={translation('Collection title')(ts)}
        aria-required="true"
        data-intercom-target={intercomTarget}
      />

      {/* Floating Action Panel */}
      <div
        className="absolute left-0 top-full mt-2 z-10 bg-card border border-border rounded px-3 py-2 flex items-center gap-2"
        style={{ boxShadow: 'var(--elevation-sm)' }}
      >
        <button
          onClick={handleSave}
          className="px-3 py-1.5 bg-accent text-accent-foreground rounded transition-all hover:opacity-90"
          style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-medium)' }}
        >
          <T kind="Save" />
        </button>
        <button
          onClick={handleCancel}
          className="px-3 py-1.5 bg-background text-foreground border border-border rounded transition-all hover:bg-muted"
          style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-medium)' }}
        >
          <T kind="Cancel" />
        </button>
      </div>

      {showMaxLengthError && (
        <div className="mt-14 text-sm text-destructive">
          <T kind="May not exceed {{maxLength}} characters" maxLength={MAX_LENGTH} />
        </div>
      )}
    </div>
  );
}

export default observer(Title);
